<template>
  <div v-if="!isLoading && !isProductsLoading">
    <p>{{$t('feeds.filter_description')}}</p>
    <transition-group name="fade" mode="out-in" tag="div">
      <div v-for="(filter, index) in filters" v-bind:key="index+1" class="mt-4 pt-3">
        <div class="dashboard-content">
          <div class="d-flex justify-content-between">
            <h6>{{$t('feeds.filter')}} #{{ index + 1 }}</h6>
            <button class="btn btn-medium btn-danger" @click="deleteFilter(index)">
              <trash-2-icon></trash-2-icon>
              <span>{{ $t('feeds.delete_filter') }}</span>
            </button>
          </div>
          <transition-group name="fade" mode="out-in" tag="div">
            <div v-for="(condition, index2) in filter.conditions" v-bind:key="index2+1"
                 class="d-flex align-items-center my-4 pt-3">
              <div class="condition-column1 d-flex justify-content-center align-items-center mr-4">
                <div v-if="index2 === 0">{{ $t('feeds.include') }}</div>
                <div v-else class="custom-label condition-and-label mx-4 bg-orange-light">{{ $t('conditions.and')}}</div>
              </div>
              <div class="condition-column2">
                <multiselect
                  @select="onFilteringFieldSelect($event, condition)"
                  label="label"
                  v-model="condition.filteringField"
                  :allow-empty="false"
                  :deselect-label="''"
                  :options="filteringFields"
                  :placeholder="$t('feeds.choose_variable')"
                  :searchable="true"
                  :select-label="''"
                  :selected-label="''"
                  :value="condition.columnName">
                  <template slot="option" slot-scope="props">
                    <span v-if="props.option.type === 'field'" class="option__title d-flex align-items-center justify-content-between bg-orange-light w-100 py-0 pl-4">{{ props.option.label }}</span>
                    <span v-if="props.option.type === 'source'" class="option__title d-flex align-items-center justify-content-between bg-purple w-100 py-0 pl-4">{{ props.option.label }}</span>
                  </template>
                  <span slot="noResult">{{ $t('global.multiselect_search_not_found') }}</span>
                </multiselect>
              </div>
              <div class="condition-column3 mx-4">
                <multiselect
                  @select="onFilteringTypeSelect($event, condition)"
                  label="label"
                  v-model="condition.filteringType"
                  :allow-empty="false"
                  :deselect-label="''"
                  :options="conditionTypes"
                  :placeholder="$t('global.type')"
                  :searchable="false"
                  :select-label="''"
                  :selected-label="''"
                  :value="condition.conditionType">
                </multiselect>
              </div>
              <div class="condition-column4 mr-4">
                <div v-if="condition.filteringType.value === 'greater then' || condition.filteringType.value === 'lower then'">
                  <input @change="validateNumber(condition)" v-model="condition.conditionString" class="form-control w-100" name="conditionString" type="text" :placeholder="$t('global.value')">
                </div>
                <div v-else-if="condition.filteringType.value === 'regex'">
                  <input @change="validateRegex(condition)" v-model="condition.conditionString" class="form-control w-100" name="conditionString" type="text" :placeholder="$t('global.value')">
                </div>
                <div v-else>
                  <input
                    v-show="condition.filteringType.value !== 'empty' && condition.filteringType.value !== 'not empty'"
                    v-model="condition.conditionString" class="form-control w-100" name="conditionString" type="text"
                    :placeholder="$t('global.value')">
                </div>
              </div>
              <div class="condition-column5">
                <button class="btn btn-orange-dark w-100" @click="resetCondition(condition)">
                  <x-circle-icon></x-circle-icon>
                  <span>Reset</span>
                </button>
              </div>
              <div class="condition-column6 ml-4">
                <button class="btn btn-danger w-100" v-show="index2 > 0" @click="deleteFilterCondition(filter, index2)">
                  <x-circle-icon></x-circle-icon>
                  <span>{{ $t('global.delete') }}</span>
                </button>
              </div>
              <div class="condition-column7 d-flex justify-content-center align-items-center ml-2">
                <div v-if="!condition.errors.required && !condition.errors.regex && !condition.errors.numeric">
                  <img class="icon-small" :id="'tooltip-condition-validation-success'+index+index2" v-bind:src="'/app/images/icons/success.svg'"/>
                  <b-popover :target="'tooltip-condition-validation-success'+index+index2" variant="success" triggers="hover" placement="bottomleft">
                    <p>{{ $t('conditions.condition_validated_successfully') }}</p>
                  </b-popover>
                </div>
                <div v-else>
                  <img class="icon-small" :id="'tooltip-condition-validation-error'+index+index2" v-bind:src="'/app/images/icons/error.svg'"/>
                  <b-popover :target="'tooltip-condition-validation-error'+index+index2" variant="danger" triggers="hover" placement="bottomleft">
                    <p v-show="condition.errors.required">{{ $t('conditions.condition_missing_field_values') }}</p>
                    <p v-show="condition.errors.regex">{{ $t('conditions.condition_invalid_regex') }}</p>
                    <p v-show="condition.errors.numeric">{{ $t('conditions.condition_must_be_numeric_value') }}</p>
                  </b-popover>
                </div>
              </div>
            </div>
          </transition-group>
          <div class="mt-4 pt-4">
            <button class="btn btn-medium btn-orange" @click="addFilterCondition(filter)">
              <plus-circle-icon></plus-circle-icon>
              <span>{{$t('feeds.add_condition')}}</span>
            </button>
          </div>
        </div>
        <transition name="fade" mode="out-in">
          <div v-show="index < filters.length - 1" class="custom-label condition-or-label bg-orange-light">{{ $t('conditions.or') }}</div>
        </transition>
      </div>
    </transition-group>
    <div class="d-flex justify-content-between mt-4 pt-3">
      <button class="btn btn-medium btn-orange" @click="addFilter">
        <plus-circle-icon></plus-circle-icon>
        <span>{{$t('feeds.add_filter')}}</span>
      </button>
      <div class="d-flex" v-if="$route.params.feedId !== undefined">
        <router-link
          :to="{ name: 'feed.edit.rules', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}"
          class="mx-4"
          v-bind:class="{ 'disabled-router-link': !isValid }">
          <button :disabled="!isValid" class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <router-link
          :to="{ name: 'feed.edit.tagging', params: { id: $route.params.projectId, feedId: feed.id, feed: feed }}"
          class="ml-2"
          v-bind:class="{ 'disabled-router-link': !isValid }">
          <button :disabled="!isValid" class="btn btn-large btn-orange-dark">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{ $t('global.next') }}</span>
          </button>
        </router-link>
      </div>
      <div class="d-flex" v-else>
        <router-link :to="{ name: 'feed.create.rules', params: { id: $route.params.projectId, feed: feed }}" class="mx-4" v-bind:class="{ 'disabled-router-link': !isValid }">
          <button :disabled="!isValid" class="btn btn-large btn-gray-dark">
            <arrow-left-circle-icon></arrow-left-circle-icon>
            <span>{{ $t('global.back') }}</span>
          </button>
        </router-link>
        <router-link :to="{ name: 'feed.create.tagging', params: { id: $route.params.projectId, feed: feed }}" :class="{ 'disabled-router-link': !isValid }">
          <button :disabled="!isValid" class="btn btn-large btn-orange-dark">
            <arrow-right-circle-icon></arrow-right-circle-icon>
            <span>{{ $t('global.next') }}</span>
          </button>
        </router-link>
      </div>
    </div>
  </div>
  <div v-else>
    <div class="spinner-border text-orange" role="status"></div>
  </div>
</template>

<script>
import { ArrowLeftCircleIcon, ArrowRightCircleIcon, PlusCircleIcon, Trash2Icon, XCircleIcon } from 'vue-feather-icons'
import Multiselect from 'vue-multiselect'
import feeds from '@/mixins/feeds'

export default {
  name: 'Filters',

  data () {
    return {
      filters: this.feed.config.filterRules,
      isValid: true
    }
  },

  props: {
    feed: { required: true, type: Object }
  },

  mixins: [feeds],

  created () {
    this.initFilters(this.feed.config.mappingRules, this.feed.feedType)
    if (this.filters.length > 0) {
      this.filters.forEach((filter) => {
        filter.conditions.forEach((condition) => {
          if (condition.incomplete) this.isValid = false
        })
      })
    }
  },

  methods: {
    addFilter () {
      this.filters.push({
        conditions: [{
          columnName: '',
          conditionType: '',
          conditionString: '',
          errors: { numeric: false, regex: false, required: false },
          filteringField: '',
          filteringType: ''
        }]
      })
      this.isValid = false
    },

    deleteFilter (index) {
      this.filters.splice(index, 1)
    },

    addFilterCondition (filter) {
      filter.conditions.push({
        columnName: '',
        conditionType: '',
        conditionString: '',
        errors: { numeric: false, regex: false, required: false },
        filteringField: '',
        filteringType: ''
      })
      this.isValid = false
    },

    deleteFilterCondition (filter, index) {
      filter.conditions.splice(index, 1)
    },

    resetCondition (condition) {
      condition.filteringField = ''
      condition.filteringType = ''
      condition.columnName = ''
      condition.conditionType = ''
      condition.conditionString = ''
      condition.errors = { required: false, regex: false, numeric: false }
    },

    onFilteringFieldSelect (filteringField, condition) {
      condition.columnName = filteringField.value
    },

    onFilteringTypeSelect (filteringType, condition) {
      condition.conditionType = filteringType.value
      if (filteringType.value === 'empty' || filteringType.value === 'not empty') condition.conditionString = ''
    },

    validateRegex (condition) {
      let isValid = true
      try {
        const newRegExp = new RegExp(condition.conditionString)
        console.log(newRegExp)
      } catch (e) {
        isValid = false
      }
      if (!isValid) {
        this.isValid = false
        condition.errors.regex = true
      } else {
        condition.errors.regex = false
      }
    },

    validateNumber (condition) {
      if (isNaN(condition.conditionString)) {
        this.isValid = false
        condition.errors.numeric = true
      } else {
        this.isValid = true
        condition.errors.numeric = false
      }
    }
  },

  watch: {
    filters: {
      handler (newVal) {
        this.isValid = true
        newVal.forEach((filter) => {
          filter.conditions.forEach((condition) => {
            if (condition.conditionType !== '') {
              if ((condition.filteringType.value === 'empty') || (condition.filteringType.value === 'not empty')) {
                if ((condition.filteringField === null) || (condition.filteringField === undefined)) {
                  this.isValid = false
                  condition.errors.required = true
                } else {
                  condition.errors.required = false
                }
              } else {
                if ((condition.filteringField === null) || (condition.filteringField === undefined) || (condition.conditionString === '')) {
                  this.isValid = false
                  condition.errors.required = true
                } else {
                  condition.errors.required = false
                }
              }
            } else {
              this.isValid = false
              condition.errors.required = true
            }
          })
        })
      },
      deep: true
    }
  },

  components: { ArrowLeftCircleIcon, ArrowRightCircleIcon, Multiselect, PlusCircleIcon, Trash2Icon, XCircleIcon }
}
</script>
